import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import { Button, Icon, Tooltip } from '@column/column-ui-kit';

import { PopoverFilter, type PopoverFilterEntry } from '~/components/Popover';
import { SearchBar } from '~/components/SearchBar';
import { useScrollStore } from '~/stores/Scroll';

export interface ToolbarProps {
  filterElement?: ReactNode;
  filters?: PopoverFilterEntry[];
  onFiltersChange?: (filters: PopoverFilterEntry[]) => void;
  onSearchSubmit?: (value: string) => void;
  searchValue?: string;
  searchTooltip?: ReactNode;
  searchShowReset?: boolean;
  searchLoading?: boolean;
  action?: ReactNode;
  className?: string;
}

const Wrapper = styled.div<{ $isScrolled: boolean }>`
  display: flex;
  gap: 16px;
  padding: 0 var(--page-margin) 12px;
  justify-content: space-between;
  background: ${({ theme }) => theme.background};
  border-bottom: 1px solid;
  border-bottom-color: transparent;
  position: sticky;
  top: 0;
  z-index: 11;
  margin-top: -1px;
  transition: border-bottom-color 0.2s;

  ${({ $isScrolled }) =>
    $isScrolled &&
    css`
      border-bottom-color: ${({ theme }) => theme.secondary.blendToBackground(150)};
    `}
`;

const StyledPopoverFilter = styled(PopoverFilter)`
  --popover-top: 36px;
  --popover-left: 24px;
  z-index: 10;
`;

const Count = styled.div`
  width: 16px;
  height: 16px;
  background-color: ${({ theme }) => theme.primary.background};
  color: ${({ theme }) => theme.primary.foreground};
  font-size: 9px;
  font-weight: bold;
  line-height: 16px;
  text-align: center;
  border-radius: 3px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  position: relative;
  container-type: inline-size;
  container-name: table-actions;
`;

export const Toolbar: React.FC<ToolbarProps> = ({
  filterElement,
  filters = [],
  onFiltersChange,
  onSearchSubmit,
  searchValue,
  searchTooltip,
  searchShowReset,
  searchLoading,
  action,
  className,
}) => {
  const isScrolled = (useScrollStore((state) => state.pageFrameScroll) ?? 0) > 0;
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [searchInput, setSearchInput] = useState(searchValue ?? '');
  const [searchFocus, setSearchFocus] = useState(false);

  if (!filterElement && filters.length === 0 && !onSearchSubmit && !action) {
    return null;
  }

  const activeFilterCount = filters.filter((entry) => entry.show === true).length;

  const searchBar = onSearchSubmit ? (
    <SearchBar
      value={searchInput}
      onChange={setSearchInput}
      onSearchSubmit={onSearchSubmit}
      onFocusChange={setSearchFocus}
      searchShowReset={searchShowReset}
      placeholder="Search"
      isLoading={searchLoading}
    />
  ) : null;

  return (
    <Wrapper $isScrolled={isScrolled} className={className}>
      {filterElement}
      {filters.length > 0 && (
        <Button variant="secondary" size="small" icon={<Icon.Filter />} onClick={() => setShowFilterPopover(true)}>
          Add Filter
          {activeFilterCount > 0 && <Count>{activeFilterCount}</Count>}
        </Button>
      )}
      <StyledPopoverFilter
        show={filters.length > 0 && showFilterPopover}
        options={filters}
        onOptionChange={onFiltersChange}
        onClose={() => setShowFilterPopover(false)}
        onDone={() => setShowFilterPopover(false)}
        onReset={() => onFiltersChange?.(filters.map((entry) => ({ ...entry, show: false })))}
      />
      {searchBar &&
        (searchTooltip ? (
          <Tooltip
            offsetX={-22}
            offsetArrowX={-92}
            placement="top-start"
            content={searchTooltip}
            isOpen={searchFocus && searchInput.length < 1}
          >
            {searchBar}
          </Tooltip>
        ) : (
          searchBar
        ))}
      <Actions>{action}</Actions>
    </Wrapper>
  );
};
